import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import BreadCrumbs from '../../SharedComponents/BreadCrumbs';
import Loading from '../../SharedComponents/Loading';
import Modal from '../../SharedComponents/Modal';
import CheckCircle from '../../SharedComponents/Svg/CheckCircle';
import XCircle from '../../SharedComponents/Svg/XCircle';
import Eye from '../../SharedComponents/Svg/Eye';
import Share from '../../SharedComponents/Svg/Share';
import Trash from '../../SharedComponents/Svg/Trash';
import {
  getKhareefPhoto,
  setKhareefPhotoStatus,
  deleteKhareefPhoto,
  errorMessage,
  STATUS,
  STATUS_META,
} from './ApiKhareefPhotos';

export default function KhareefPhotoDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();

  const [photo, setPhoto] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery(['khareefPhoto', id], getKhareefPhoto, {
    refetchOnMount: true,
  });

  // Driven off `data` rather than an onSuccess callback: coming back to a row
  // that was already opened is a cache hit, so no fetch happens and onSuccess
  // never fires again - which left the page rendering its empty state for a
  // submission that had loaded fine a moment earlier.
  useEffect(() => {
    setPhoto(data?.data ?? null);
  }, [data]);

  // Re-read this row, and the list behind it, after every change.
  const refresh = () => {
    queryClient.invalidateQueries(['khareefPhoto', id]);
    queryClient.invalidateQueries(['khareefPhotos']);
  };

  const statusMutation = useMutation(setKhareefPhotoStatus, {
    onSuccess: ({ data: updated }) => {
      setPhoto((prev) =>
        prev
          ? {
              ...prev,
              status: updated.status,
              rejectionReason: updated.status === STATUS.REJECTED ? updated.reason : prev.rejectionReason,
            }
          : prev,
      );
      toast.success(t(STATUS_META[updated.status].label));
      refresh();
    },
    onError: (err) => toast.error(t(errorMessage(err)) || t('Something went wrong')),
  });

  const deleteMutation = useMutation(deleteKhareefPhoto, {
    onSuccess: () => {
      toast.success(t('Photo deleted'));
      queryClient.invalidateQueries(['khareefPhotos']);
      history.push('/khareef-photos/manage');
    },
    onError: (err) => toast.error(t(errorMessage(err)) || t('Something went wrong')),
  });

  const changeStatus = (status) => statusMutation.mutate({ id: photo.id, status });

  const submitReject = () => {
    if (!rejectReason.trim()) {
      toast.error(t('Please enter a reason for rejection'));
      return;
    }
    statusMutation.mutate(
      { id: photo.id, status: STATUS.REJECTED, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          setShowReject(false);
          setRejectReason('');
        },
      },
    );
  };

  if (isLoading) return <Loading />;
  if (isError)
    return <div className="text-red-600">{t(errorMessage(error)) || t('Something went wrong')}</div>;

  if (!photo) {
    return (
      <>
        <header className="mb-4">
          <BreadCrumbs
            to="/khareef-photos/manage"
            name={t('Manage Submissions')}
            backTo="/khareef-photos/manage"
          />
        </header>
        <div className="bg-white px-6 py-16 rounded-lg shadow text-center text-gray-400">
          {t('No submissions found')}
        </div>
      </>
    );
  }

  const meta = STATUS_META[photo.status];

  return (
    <>
      <header className="mb-4">
        <BreadCrumbs
          to="/khareef-photos/manage"
          name={t('Manage Submissions')}
          morePages={[{ to: `/khareef-photos/manage/${photo.id}`, name: t('Photo Details') }]}
          backTo="/khareef-photos/manage"
        />
      </header>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Image */}
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="block bg-gray-100"
            title={t('Image Preview')}
          >
            <img src={photo.url} alt={photo.participant} className="w-full h-full max-h-[28rem] object-cover" />
          </button>

          {/* Meta */}
          <div className="p-6 flex flex-col">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-2xl font-bold text-gray-800">{photo.title}</h2>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${meta.className}`}>{t(meta.label)}</span>
            </div>

            <dl className="mt-6 space-y-4 text-sm">
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Participant')}</dt>
                <dd className="font-medium text-gray-800">{photo.participant}</dd>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Mobile Number')}</dt>
                <dd className="font-medium text-gray-800" dir="ltr">{photo.mobileNumber || '—'}</dd>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Instagram')}</dt>
                <dd className="font-medium text-gray-800" dir="ltr">
                  {photo.instagram ? (
                    <a
                      href={`https://instagram.com/${photo.instagram}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-blue-600 hover:underline"
                    >
                      @{photo.instagram}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Reference Number')}</dt>
                <dd className="font-medium text-gray-800" dir="ltr">{photo.referenceNumber || '—'}</dd>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Photo title')}</dt>
                <dd className="font-medium text-gray-800">{photo.title}</dd>
              </div>
              {photo.description && (
                <div className="border-b border-gray-100 pb-2">
                  <dt className="text-gray-500 mb-1">{t('Description')}</dt>
                  <dd className="font-medium text-gray-800">{photo.description}</dd>
                </div>
              )}
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Location')}</dt>
                <dd className="font-medium text-gray-800">{photo.location}</dd>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Submitted at')}</dt>
                <dd className="font-medium text-gray-800">{photo.submittedAt}</dd>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <dt className="text-gray-500">{t('Status')}</dt>
                <dd className="font-medium text-gray-800">{t(meta.label)}</dd>
              </div>
              {photo.status === STATUS.REJECTED && photo.rejectionReason && (
                <div className="bg-red-50 text-red-800 rounded p-3">
                  <dt className="font-semibold mb-1">{t('Rejection Reason')}</dt>
                  <dd>{photo.rejectionReason}</dd>
                </div>
              )}
            </dl>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 mt-auto pt-6">
              {(photo.status === STATUS.PENDING || photo.status === STATUS.REJECTED) && (
                <button
                  onClick={() => changeStatus(STATUS.APPROVED)}
                  className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded px-3 py-2"
                >
                  <CheckCircle className="w-4 h-4" /> {t('Approve')}
                </button>
              )}
              {photo.status === STATUS.APPROVED && (
                <button
                  onClick={() => changeStatus(STATUS.PUBLISHED)}
                  className="flex items-center gap-1 text-sm bg-green-50 text-green-700 hover:bg-green-100 rounded px-3 py-2"
                >
                  <Share className="w-4 h-4" /> {t('Publish')}
                </button>
              )}
              {photo.status === STATUS.PUBLISHED && (
                <button
                  onClick={() => changeStatus(STATUS.APPROVED)}
                  className="flex items-center gap-1 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 rounded px-3 py-2"
                >
                  <Eye className="w-4 h-4" /> {t('Review')}
                </button>
              )}
              {photo.status !== STATUS.REJECTED && (
                <button
                  onClick={() => {
                    setRejectReason('');
                    setShowReject(true);
                  }}
                  className="flex items-center gap-1 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded px-3 py-2"
                >
                  <XCircle className="w-4 h-4" /> {t('Reject')}
                </button>
              )}
              <button
                onClick={() => setShowDelete(true)}
                className="flex items-center gap-1 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded px-3 py-2"
              >
                <Trash className="w-4 h-4" /> {t('Delete')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject dialog with rejection reason */}
      <Modal
        isOpen={showReject}
        setIsOpen={setShowReject}
        title={t('Reject Photo')}
        confirmTitle={t('Reject')}
        onSubmit={submitReject}
        submitType="button"
        isLoading={statusMutation.isLoading}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            {t('You are rejecting the submission by')} <span className="font-semibold">{photo.participant}</span>.
          </p>
          <label className="block text-sm font-medium text-gray-700">{t('Rejection Reason')}</label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            className="w-full border rounded p-2 text-sm"
            placeholder={t('Enter the reason for rejecting this photo')}
          />
        </div>
      </Modal>

      {/* Delete confirmation dialog */}
      <Modal
        isOpen={showDelete}
        setIsOpen={setShowDelete}
        title={t('Delete Photo')}
        confirmTitle={t('Delete')}
        onSubmit={() => deleteMutation.mutate(photo.id)}
        submitType="button"
        isLoading={deleteMutation.isLoading}
      >
        <p className="text-sm text-gray-600">
          {t('Are you sure you want to delete this photo? This action cannot be undone.')}
        </p>
      </Modal>

      {/* Image preview dialog */}
      <Modal
        isOpen={showPreview}
        setIsOpen={setShowPreview}
        title={photo.title}
        cancelTitle={t('Close')}
        hideSubmit
        className="sm:max-w-3xl"
      >
        <img src={photo.url} alt={photo.participant} className="w-full max-h-[70vh] object-contain rounded" />
      </Modal>
    </>
  );
}
